import { useEffect, useState } from "react";
import { Loader2, RefreshCw, Info } from "lucide-react";
import { api } from "@/lib/api";

const GRADE_STYLE = {
  A: "bg-emerald-100 text-emerald-800 border-emerald-300",
  B: "bg-sky-100 text-sky-800 border-sky-300",
  C: "bg-amber-100 text-amber-800 border-amber-300",
  D: "bg-orange-100 text-orange-800 border-orange-300",
  E: "bg-rose-100 text-rose-800 border-rose-300",
};

function GradeBadge({ grade, size = "sm" }) {
  const letter = String(grade || "?").charAt(0).toUpperCase();
  const cls = GRADE_STYLE[letter] || "bg-stone-100 text-stone-800 border-stone-300";
  const sz = size === "lg" ? "w-14 h-14 text-2xl"
           : size === "sm" ? "w-6 h-6 text-[11px]"
           : "w-8 h-8 text-sm";
  return (
    <div className={`inline-flex items-center justify-center rounded-lg border font-bold ${sz} ${cls}`}>
      {grade}
    </div>
  );
}

/**
 * Uni-Wide Coverage Card — grades progress on the *context* metric
 * (`universities.entry_requirements_typical`) per country. Kept
 * deliberately separate from the main course-level coverage grade so
 * a strong context roll-up can never mask weak per-course data.
 *
 * Grade rubric (gentler than course-level entry-reqs on purpose —
 * uni-typical is context data; 30% coverage is genuinely useful):
 *   A ≥ 50%, B ≥ 30%, C ≥ 15%, D ≥ 5%, else E.
 */
export default function UniWideCoverageCard() {
  const [data, setData] = useState(null);
  const [err, setErr]   = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    setLoading(true); setErr(null);
    try {
      const r = await api.get("/admin/progress/coverage-assessment");
      setData(r.data?.uni_entry_req || null);
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || "Failed to load");
    } finally { setLoading(false); }
  };
  useEffect(() => { refresh(); }, []);

  if (loading && !data) {
    return (
      <div
        data-testid="uni-wide-coverage-loading"
        className="border border-stone-200 rounded-xl bg-white p-4 mb-6 flex items-center gap-3"
      >
        <Loader2 className="w-4 h-4 animate-spin text-stone-500" />
        <div className="text-sm text-stone-500">Loading uni-wide coverage…</div>
      </div>
    );
  }
  if (err) {
    return (
      <div className="border border-rose-200 rounded-xl bg-rose-50 p-4 mb-6 text-sm text-rose-800">
        {err}
      </div>
    );
  }
  if (!data?.by_country?.length) return null;

  const total   = data.by_country.reduce((s, r) => s + r.unis, 0);
  const typical = data.by_country.reduce((s, r) => s + r.typical, 0);

  return (
    <div
      data-testid="tile-uni-wide-coverage"
      className="border border-stone-300 rounded-xl bg-white p-4 mb-6 shadow-sm"
    >
      <div className="flex items-start justify-between mb-3">
        <div>
          <div className="text-[10px] font-underground-sc tracking-widest text-stone-500 mb-1">
            Uni-wide entry-req range · context coverage
          </div>
          <div className="text-sm text-stone-800 leading-snug">
            {typical.toLocaleString()} of {total.toLocaleString()} universities have an approved
            uni-wide A-level range on record (context only — never used as per-course data).
          </div>
        </div>
        <button
          type="button"
          onClick={refresh}
          data-testid="uni-wide-coverage-refresh"
          className="text-stone-400 hover:text-stone-700"
          title="Recompute"
        >
          {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
        </button>
      </div>

      <div className="grid grid-cols-3 sm:grid-cols-5 lg:grid-cols-9 gap-2">
        {data.by_country.map(r => (
          <div
            key={r.country}
            data-testid={`uni-wide-country-${r.country}`}
            className="flex items-center gap-2 border border-stone-100 rounded-md px-2 py-1.5 bg-stone-50/60"
          >
            <GradeBadge grade={r.grade} size="sm" />
            <div className="min-w-0">
              <div className="text-[11px] font-semibold text-stone-800 leading-tight">{r.country}</div>
              <div className="text-[10px] text-stone-500 font-mono leading-tight">
                {r.typical}/{r.unis} · {r.pct}%
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-3 flex items-start gap-1.5 text-[11px] text-stone-500">
        <Info className="w-3 h-3 mt-0.5 flex-none" />
        <span>{data.note}</span>
      </div>
    </div>
  );
}
